<template>
  <div class="journal-container d-flex">
    <div
      v-if="$vuetify.breakpoint.mdAndUp"
      class="col-2 pa-0 ma-0"
    >
      <JournalSectionsTree
        :spotlight-sections="treeSections(processedSpotlightJournals, 0)"
        :group-standalone-sections="treeSections(
          processedGroupStandaloneJournals,
          processedSpotlightJournals.length,
        )"
        :user-reflection-sections="treeSections(
          processedUserReflectionJournals,
          processedSpotlightJournals.length + processedGroupStandaloneJournals.length,
        )"
        :user-standalone-sections="treeSections(
          processedUserStandaloneJournals,
          processedSpotlightJournals.length + processedGroupStandaloneJournals.length + processedUserReflectionJournals.length
        )"
      />
    </div>
    <ScrollBar
      :class="$vuetify.breakpoint.mdAndUp ? 'col-10' : 'col-12'"
      id="journal-scrollbar"
      @onScrollY="onScroll"
      height="calc(100vh - 72px)"
      max-height="calc(100vh - 72px)"
    >
      <div class="scrollbar-content d-flex justify-center">
        <div class="journal-section-content">
          <JournalOrgSection
            :recommendations="orgJournalDocuments"
          />
          <JournalSectionList
            :title="$t('views.journal.sections.spotlight')"
            :journals="processedSpotlightJournals"
          >
            <div class="alpha-card d-flex justify-start align-center">
              <v-icon class="alpha-icon">
                gpp_maybe
              </v-icon>
              <span class="alpha-comment px-1">
                {{ $t('views.journal.messages.beta') }}
              </span>
            </div>
          </JournalSectionList>
          <JournalSectionList
            :title="$t('views.journal.sections.groups')"
            :journals="processedGroupStandaloneJournals"
            :starting-index="processedSpotlightJournals.length"
          />
          <JournalSectionList
            :title="$t('views.journal.sections.reflections')"
            :journals="processedUserReflectionJournals"
            :starting-index="processedSpotlightJournals.length +
              processedGroupStandaloneJournals.length
            "
          />
          <JournalSectionList
            :title="$t('views.journal.sections.personal')"
            :journals="processedUserStandaloneJournals"
            :starting-index="processedSpotlightJournals.length +
              processedGroupStandaloneJournals.length +
              processedUserReflectionJournals.length
            "
          />
        </div>
      </div>
    </ScrollBar>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import FocusedElementWatcher from '@/shared/components/scrollbar/FocusedElementWatcher';
import ScrollBar from '@/shared/components/scrollbar/ScrollBar';
import HashPositionMixin from '../../mixins/HashPositionMixin';
import { types } from '../../store/rootTypes';
import JournalSectionsTree from './JournalSectionsTree';
import JournalSectionList from './JournalSectionList';
import JournalOrgSection from './JournalOrgSection';

export const prepareJournalDocumentForDocumentCard = (journalDocument) => ({
  ...journalDocument,
  documentDetails: {
    ...journalDocument.documentData,
    documentType: 'document',
  },
});

export const prepareDocumentForDocumentCard = (doc) => ({
  documentId: doc.documentId,
  documentDetails: {
    documentType: 'document',
    ...doc,
  },
});

export const prepareJournalAnalysisForDocumentCard = (journalDocument) => ({
  ...journalDocument.documentData,
});

export default {
  name: 'RecommendationBasedNewspaper',
  components: {
    JournalSectionsTree,
    JournalOrgSection,
    JournalSectionList,
    ScrollBar,
  },
  mixins: [HashPositionMixin],
  data: () => ({
    journalSeenTimer: null,
    selectedSectionIndex: 0,
    disableScrollEvent: false,
  }),
  computed: {
    ...mapGetters({
      recommendationBasedJournal: types.recommendationBasedJournals.getters.recommendationBasedJournal,
      spotlightJournal: types.recommendationBasedJournals.getters.spotlightJournal,
      orgJournal: types.recommendationBasedJournals.getters.orgJournal,
      userStandaloneJournals: types.recommendationBasedJournals.getters.userStandaloneJournals,
      userReflectionJournals: types.recommendationBasedJournals.getters.userReflectionJournals,
      groupStandaloneJournals: types.recommendationBasedJournals.getters.groupStandaloneJournals,
      groups: types.authentication.getters.groups,
      pipelinedJournalActivated: types.featureSwitches.getters.pipelinedJournal,
    }),
    processedUserStandaloneJournals() {
      return this.processStandaloneJournals(this.userStandaloneJournals);
    },
    processedGroupStandaloneJournals() {
      return this.processStandaloneJournals(this.groupStandaloneJournals);
    },
    processedUserReflectionJournals() {
      return this.processStandaloneJournals(this.userReflectionJournals);
    },
    spotlightJournals() {
      return {
        groupsAnalyses: this.processEntityJournal(this.spotlightJournal.groupsAnalyses),
        similarToGroupsAnalyses: this.processEntityJournal(this.spotlightJournal.similarToGroupsAnalyses),
        similarToUserAnalyses: this.processEntityJournal(this.spotlightJournal.similarToUserAnalyses),
        organization: this.processEntityJournal(this.spotlightJournal.organization),
      };
    },
    processedSpotlightJournals() {
      const journals = [];
      if (this.spotlightJournals.organization) {
        journals.push(this.spotlightJournals.organization);
      }
      if (this.spotlightJournals.groupsAnalyses) {
        journals.push(this.spotlightJournals.groupsAnalyses);
      }
      if (this.spotlightJournals.similarToUserAnalyses) {
        journals.push(this.spotlightJournals.similarToUserAnalyses);
      }
      if (this.spotlightJournals.similarToGroupsAnalyses) {
        journals.push(this.spotlightJournals.similarToGroupsAnalyses);
      }
      return journals;
    },
    allJournals() {
      return [
        ...this.processedSpotlightJournals,
        ...this.processedUserStandaloneJournals,
        ...this.processedGroupStandaloneJournals,
        ...this.processedUserReflectionJournals,
      ];
    },
    orgJournalDocuments() {
      if (this.orgJournal) {
        return this.orgJournal.map(prepareDocumentForDocumentCard);
      }
      return [];
    },
    journalGroups() {
      if (this.recommendationBasedJournal.groups === null || typeof this.recommendationBasedJournal.groups !== 'object') {
        return [];
      }
      return Object.entries(this.recommendationBasedJournal.groups)
        .map(([groupId, recommendations]) => ({ id: groupId, recommendations }));
    },
  },
  methods: {
    ...mapActions({
      markUserJournalAsRead: types.authentication.actions.markUserJournalAsRead,
    }),
    processStandaloneJournals(journals) {
      const journalWithRecommendations = [];
      const journalWithoutRecommendation = [];

      journals.forEach(entityJournal => {
        const journal = this.processEntityJournal(entityJournal);

        if (journal.recommendations.length === 0) {
          journalWithoutRecommendation.push(journal);
        } else {
          journalWithRecommendations.push(journal);
        }
      });

      // Section without recommendation are still shown in the journal, but at the end.
      return journalWithRecommendations.concat(journalWithoutRecommendation);
    },
    processEntityJournal(entityJournal) {
      if (!entityJournal) return undefined;

      const { entityType, id, editable } = entityJournal.journal;
      const title = this.getJournalTitle(entityJournal);
      const type = this.getJournalType(entityJournal);
      const recommendations = entityJournal.recommendations.map(rec => this.processRecommendation(rec));

      return {
        id,
        title,
        type,
        entityType,
        recommendations,
        editable,
      };
    },
    processRecommendation(data) {
      const { documentData, recommendation } = data;
      if (documentData === undefined) {
        return { documentData: {} };
      }
      const basicInformation = {
        ...documentData,
        recommendation,
      };

      if (documentData.documentType === 'document') {
        return prepareDocumentForDocumentCard(basicInformation);
      }

      return basicInformation;
    },
    treeSections(journals, startingIndex) {
      const allJournalsMap = journals.map((journal, index) => ({
        id: journal.id,
        title: journal.title,
        focused: (startingIndex + index) === this.selectedSectionIndex,
        select: () => {
          const id = `journal-card-iterator-${startingIndex + index}`;
          const elem = document.getElementById(id);
          this.disableScrollEvent = true;
          elem.scrollIntoView();
          // To be aligned with the tree title (Journal Beta).
          elem.offsetParent.scrollTop -= 10;
          // Wait the animation that may start an unwanted scroll event.
          setTimeout(() => { this.disableScrollEvent = false; }, 100);
          this.selectedSectionIndex = startingIndex + index;
        },
      }));
      return allJournalsMap;
    },
    getJournalTitle(entityJournal) {
      return entityJournal.journal.title; // TODO
    },
    getJournalType(entityJournal) {
      if (entityJournal.journal.origin && entityJournal.journal.origin.type) {
        return entityJournal.journal.origin.type;
      }
      return 'system';
    },
    onScroll() {
      if (this.disableScrollEvent) return;

      const watcher = new FocusedElementWatcher('journal-scrollbar');
      const elementIds = this.allJournals.map((_, index) => `journal-card-iterator-${index}`);
      const selectedElement = watcher.findFocusedElementFrom(elementIds);

      this.selectedSectionIndex = elementIds.indexOf(selectedElement.id);
    },
  },
  beforeDestroy() {
    // TODO register page bounce instead of just clearTimeout
    clearTimeout(this.journalSeenTimer);
  },
  created() {
    this.journalSeenTimer = setTimeout(this.markUserJournalAsRead, 10000);
    this.resetPosition(); // is there any real use for this?
  },
};
</script>
<style scoped>
.journal-container {
  width: 100%;
}
.journal-section-content {
  max-width: 1864px;
  width: 100%;
}
.scrollbar-content {
  max-width: 100vw;
}
.alpha-card {
  padding: 12px;
  margin-left: 12px;
  margin-right: 12px;
  border-radius: 4px;
  margin-bottom: 8px;
  background: rgba(0, 0, 0, 0.08);
}
.alpha-comment {
  color: rgba(0, 0, 0, 0.5);
  font-size: 14px;
  font-weight: 300;
}
.alpha-icon {
  color: rgba(51, 72, 196, 0.6);
  font-size: 24px;
}
</style>
